import { useEffect } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  useAuthContext,
  usePasswordFieldToggle,
  useRecaptcha,
  useSignupForm,
  useSignupSubmission,
} from "hooks";

import { ExternalInviteSignUp } from "./ExternalInviteSignUp";
import { E164_MIN_DIGIT_COUNT } from "../../../../constants";

import type { SignUpData } from "../SignUp/types";

interface ExternalInviteSignUpContainerProps {
  signUpRole: Extract<RoleValue, "CARRIER" | "SHIPPER">;
}

export function ExternalInviteSignUpContainer(
  props: ExternalInviteSignUpContainerProps,
) {
  const { signUpRole } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthContext();
  const { passwordIsVisible, handleShowPasswordChange } =
    usePasswordFieldToggle();

  const { recaptchaToken, executeReCaptcha } = useRecaptcha();

  const {
    form,
    formError,
    termsAccepted,
    handleFormChange,
    handleTermsChange,
    validateEmailMatch,
  } = useSignupForm<Omit<SignUpData, "persona">>({
    initialFormState: {
      email: "",
      confirmEmail: "",
      password: "",
      firstName: "",
      lastName: "",
      workPhoneNumber: "",
      workPhoneNumberExtension: "",
      cellPhoneNumber: "",
      recaptchaToken,
    },
  });

  const { loading, signupError, handleSignup, setSignupError } =
    useSignupSubmission({
      executeReCaptcha,
    });

  const shipperCompanyName = searchParams.get("shipper_company_name");

  // if user is already authenticated redirect then to the homepage
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const onExternalSignup = () => {
    const {
      email,
      password,
      firstName,
      lastName,
      workPhoneNumber,
      workPhoneNumberExtension,
      cellPhoneNumber,
      confirmEmail,
    } = form;

    const emailMatchError = validateEmailMatch(email, confirmEmail);
    if (emailMatchError) {
      setSignupError(emailMatchError);
      return;
    }

    handleSignup({
      email,
      password,
      firstName,
      lastName,
      workPhoneNumber,
      workPhoneNumberExtension,
      cellPhoneNumber,
      recaptchaToken,
      role: signUpRole,
    });
  };

  return (
    <ExternalInviteSignUp
      signUpRole={signUpRole}
      shipperCompanyName={shipperCompanyName}
      onFormChange={handleFormChange}
      onSignup={onExternalSignup}
      workPhoneNumber={form.workPhoneNumber}
      workPhoneNumberExtension={form.workPhoneNumberExtension}
      cellPhoneNumber={form.cellPhoneNumber}
      formError={formError}
      showPassword={passwordIsVisible}
      onShowPasswordChange={handleShowPasswordChange}
      signupDisabled={
        form.email.length === 0 ||
        form.password.length === 0 ||
        form.firstName.length === 0 ||
        form.lastName.length === 0 ||
        form.workPhoneNumber.replace(/\D/g, "").length < E164_MIN_DIGIT_COUNT ||
        formError.email != null ||
        formError.password != null ||
        !termsAccepted
      }
      signupError={signupError}
      onTermsChange={handleTermsChange}
      loading={loading}
    />
  );
}
